// 本地云服务模块 - 模拟云存储，使用本地文件系统
class LocalCloudService {
  constructor() {
    this.storagePath = './altverse-data'
    this.maxFileSize = 10 * 1024 * 1024 // 10MB per file
    this.init()
  }

  // 初始化本地存储目录
  async init() {
    try {
      // 在浏览器环境中，我们使用IndexedDB作为"本地文件系统"
      if (typeof window !== 'undefined' && 'indexedDB' in window) {
        await this.initIndexedDB()
      } else {
        console.warn('IndexedDB not supported, falling back to localStorage')
      }
    } catch (error) {
      console.error('初始化本地云服务失败:', error)
    }
  }

  // 初始化IndexedDB
  async initIndexedDB() {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open('AltVerseCloud', 1)
      
      request.onerror = () => reject(request.error)
      request.onsuccess = () => {
        this.db = request.result
        console.log('✅ 本地云服务IndexedDB初始化成功')
        resolve()
      }
      
      request.onupgradeneeded = (event) => {
        const db = event.target.result
        
        // 创建对象存储
        if (!db.objectStoreNames.contains('stories')) {
          db.createObjectStore('stories', { keyPath: 'id' })
        }
        if (!db.objectStoreNames.contains('characters')) {
          db.createObjectStore('characters', { keyPath: 'id' })
        }
        if (!db.objectStoreNames.contains('files')) {
          db.createObjectStore('files', { keyPath: 'name' })
        }
        if (!db.objectStoreNames.contains('metadata')) {
          db.createObjectStore('metadata', { keyPath: 'key' })
        }
      }
    })
  }

  // 保存数据到文件
  async saveToFile(fileName, data) {
    try {
      if (this.db) {
        return await this.saveToIndexedDB(fileName, data)
      } else {
        return await this.saveToLocalStorage(fileName, data)
      }
    } catch (error) {
      console.error('保存文件失败:', error)
      throw error
    }
  }

  // 保存图片文件
  async saveImageFile(fileName, file) {
    try {
      if (this.db) {
        return await this.saveImageToIndexedDB(fileName, file)
      } else {
        return await this.saveImageToLocalStorage(fileName, file)
      }
    } catch (error) {
      console.error('保存图片文件失败:', error)
      throw error
    }
  }

  // 保存图片到IndexedDB
  async saveImageToIndexedDB(fileName, file) {
    return new Promise((resolve, reject) => {
      // 先读取文件内容
      const reader = new FileReader()
      reader.onload = () => {
        try {
          // 在文件读取完成后创建事务
          const transaction = this.db.transaction(['files'], 'readwrite')
          const store = transaction.objectStore('files')
          
          const fileData = {
            name: fileName,
            data: reader.result,
            size: file.size,
            timestamp: new Date().toISOString(),
            type: file.type,
            isImage: true
          }
          
          const request = store.put(fileData)
          
          request.onsuccess = () => {
            console.log(`✅ 图片已保存到IndexedDB: ${fileName}`)
            // 立即创建blob URL，确保一致性
            const blob = new Blob([reader.result], { type: file.type })
            const blobUrl = URL.createObjectURL(blob)
            console.log(`✅ 创建blob URL: ${blobUrl}`)
            resolve({
              name: fileName,
              url: blobUrl,
              size: file.size,
              type: file.type
            })
          }
          
          request.onerror = () => {
            console.error('保存图片到IndexedDB失败:', request.error)
            reject(request.error)
          }
          
          // 监听事务完成事件
          transaction.oncomplete = () => {
            console.log('IndexedDB事务完成')
          }
          
          transaction.onerror = () => {
            console.error('IndexedDB事务失败:', transaction.error)
            reject(transaction.error)
          }
        } catch (error) {
          console.error('创建IndexedDB事务失败:', error)
          reject(error)
        }
      }
      
      reader.onerror = () => {
        console.error('读取文件失败:', reader.error)
        reject(reader.error)
      }
      
      reader.readAsArrayBuffer(file)
    })
  }

  // 保存图片到localStorage（备用方案）
  async saveImageToLocalStorage(fileName, file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = () => {
        try {
          const imageData = {
            data: reader.result,
            size: file.size,
            type: file.type,
            timestamp: new Date().toISOString(),
            isImage: true
          }
          
          // 将图片数据分块存储
          const dataString = JSON.stringify(imageData)
          const chunks = this.chunkString(dataString, 1000000) // 1MB per chunk
          
          if (chunks.length === 1) {
            localStorage.setItem(fileName, dataString)
          } else {
            // 多块存储
            localStorage.setItem(`${fileName}_chunks`, chunks.length.toString())
            chunks.forEach((chunk, index) => {
              localStorage.setItem(`${fileName}_chunk_${index}`, chunk)
            })
          }
          
          console.log(`✅ 图片已保存到localStorage: ${fileName}`)
          // 立即创建blob URL，确保一致性
          const blob = new Blob([reader.result], { type: file.type })
          const blobUrl = URL.createObjectURL(blob)
          console.log(`✅ 创建blob URL: ${blobUrl}`)
          resolve({
            name: fileName,
            url: blobUrl,
            size: file.size,
            type: file.type
          })
        } catch (error) {
          reject(error)
        }
      }
      
      reader.onerror = () => {
        reject(reader.error)
      }
      
      reader.readAsArrayBuffer(file)
    })
  }

  // 获取图片URL
  async getImageUrl(fileName) {
    try {
      if (this.db) {
        return await this.getImageUrlFromIndexedDB(fileName)
      } else {
        return await this.getImageUrlFromLocalStorage(fileName)
      }
    } catch (error) {
      console.error('获取图片URL失败:', error)
      return null
    }
  }

  // 从IndexedDB获取图片URL
  async getImageUrlFromIndexedDB(fileName) {
    return new Promise((resolve, reject) => {
      try {
        const transaction = this.db.transaction(['files'], 'readonly')
        const store = transaction.objectStore('files')
        const request = store.get(fileName)
        
        request.onsuccess = () => {
          if (request.result && request.result.isImage) {
            try {
              // 创建blob URL
              const blob = new Blob([request.result.data], { type: request.result.type })
              const url = URL.createObjectURL(blob)
              resolve(url)
            } catch (error) {
              console.error('创建blob URL失败:', error)
              resolve(null)
            }
          } else {
            resolve(null)
          }
        }
        
        request.onerror = () => {
          console.error('从IndexedDB获取图片失败:', request.error)
          reject(request.error)
        }
        
        // 监听事务完成事件
        transaction.oncomplete = () => {
          console.log('IndexedDB读取事务完成')
        }
        
        transaction.onerror = () => {
          console.error('IndexedDB读取事务失败:', transaction.error)
          reject(transaction.error)
        }
      } catch (error) {
        console.error('创建IndexedDB读取事务失败:', error)
        reject(error)
      }
    })
  }

  // 从localStorage获取图片URL
  async getImageUrlFromLocalStorage(fileName) {
    try {
      // 检查是否是分块存储
      const chunksCount = localStorage.getItem(`${fileName}_chunks`)
      let dataString
      
      if (chunksCount) {
        // 多块读取
        const chunks = []
        for (let i = 0; i < parseInt(chunksCount); i++) {
          const chunk = localStorage.getItem(`${fileName}_chunk_${i}`)
          if (chunk) {
            chunks.push(chunk)
          }
        }
        dataString = chunks.join('')
      } else {
        // 单块读取
        dataString = localStorage.getItem(fileName)
      }
      
      if (dataString) {
        const imageData = JSON.parse(dataString)
        if (imageData.isImage) {
          // 创建blob URL
          const blob = new Blob([imageData.data], { type: imageData.type })
          const url = URL.createObjectURL(blob)
          return url
        }
      }
      
      return null
    } catch (error) {
      console.error('从localStorage获取图片URL失败:', error)
      return null
    }
  }

  // 保存到IndexedDB
  async saveToIndexedDB(fileName, data) {
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction(['files'], 'readwrite')
      const store = transaction.objectStore('files')
      
      const fileData = {
        name: fileName,
        data: data,
        size: new Blob([JSON.stringify(data)]).size,
        timestamp: new Date().toISOString(),
        type: 'application/json'
      }
      
      const request = store.put(fileData)
      
      request.onsuccess = () => {
        console.log(`✅ 数据已保存到IndexedDB: ${fileName}`)
        resolve(fileData)
      }
      
      request.onerror = () => {
        console.error('保存到IndexedDB失败:', request.error)
        reject(request.error)
      }
    })
  }

  // 保存到localStorage（备用方案）
  async saveToLocalStorage(fileName, data) {
    try {
      // 将大文件分块存储
      const dataString = JSON.stringify(data)
      const chunks = this.chunkString(dataString, 1000000) // 1MB per chunk
      
      if (chunks.length === 1) {
        localStorage.setItem(fileName, dataString)
      } else {
        // 多块存储
        localStorage.setItem(`${fileName}_chunks`, chunks.length.toString())
        chunks.forEach((chunk, index) => {
          localStorage.setItem(`${fileName}_chunk_${index}`, chunk)
        })
      }
      
      console.log(`✅ 数据已保存到localStorage: ${fileName}`)
      return { name: fileName, size: dataString.length }
    } catch (error) {
      console.error('保存到localStorage失败:', error)
      throw error
    }
  }

  // 从文件读取数据
  async loadFromFile(fileName) {
    try {
      if (this.db) {
        return await this.loadFromIndexedDB(fileName)
      } else {
        return await this.loadFromLocalStorage(fileName)
      }
    } catch (error) {
      console.error('读取文件失败:', error)
      return null
    }
  }

  // 从IndexedDB读取
  async loadFromIndexedDB(fileName) {
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction(['files'], 'readonly')
      const store = transaction.objectStore('files')
      const request = store.get(fileName)
      
      request.onsuccess = () => {
        if (request.result) {
          console.log(`✅ 从IndexedDB读取数据: ${fileName}`)
          resolve(request.result.data)
        } else {
          resolve(null)
        }
      }
      
      request.onerror = () => {
        console.error('从IndexedDB读取失败:', request.error)
        reject(request.error)
      }
    })
  }

  // 从localStorage读取（备用方案）
  async loadFromLocalStorage(fileName) {
    try {
      // 检查是否是分块存储
      const chunksCount = localStorage.getItem(`${fileName}_chunks`)
      
      if (chunksCount) {
        // 多块读取
        const chunks = []
        for (let i = 0; i < parseInt(chunksCount); i++) {
          const chunk = localStorage.getItem(`${fileName}_chunk_${i}`)
          if (chunk) {
            chunks.push(chunk)
          }
        }
        
        if (chunks.length > 0) {
          const dataString = chunks.join('')
          return JSON.parse(dataString)
        }
      } else {
        // 单块读取
        const dataString = localStorage.getItem(fileName)
        if (dataString) {
          return JSON.parse(dataString)
        }
      }
      
      return null
    } catch (error) {
      console.error('从localStorage读取失败:', error)
      return null
    }
  }

  // 删除文件
  async deleteFile(fileName) {
    try {
      if (this.db) {
        return await this.deleteFromIndexedDB(fileName)
      } else {
        return await this.deleteFromLocalStorage(fileName)
      }
    } catch (error) {
      console.error('删除文件失败:', error)
      throw error
    }
  }

  // 从IndexedDB删除
  async deleteFromIndexedDB(fileName) {
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction(['files'], 'readwrite')
      const store = transaction.objectStore('files')
      const request = store.delete(fileName)
      
      request.onsuccess = () => {
        console.log(`✅ 从IndexedDB删除文件: ${fileName}`)
        resolve()
      }
      
      request.onerror = () => {
        console.error('从IndexedDB删除失败:', request.error)
        reject(request.error)
      }
    })
  }

  // 从localStorage删除
  async deleteFromLocalStorage(fileName) {
    try {
      // 检查是否是分块存储
      const chunksCount = localStorage.getItem(`${fileName}_chunks`)
      
      if (chunksCount) {
        // 删除所有块
        for (let i = 0; i < parseInt(chunksCount); i++) {
          localStorage.removeItem(`${fileName}_chunk_${i}`)
        }
        localStorage.removeItem(`${fileName}_chunks`)
      } else {
        localStorage.removeItem(fileName)
      }
      
      console.log(`✅ 从localStorage删除文件: ${fileName}`)
    } catch (error) {
      console.error('从localStorage删除失败:', error)
      throw error
    }
  }

  // 列出所有文件
  async listFiles() {
    try {
      if (this.db) {
        return await this.listIndexedDBFiles()
      } else {
        return await this.listLocalStorageFiles()
      }
    } catch (error) {
      console.error('列出文件失败:', error)
      return []
    }
  }

  // 列出IndexedDB文件
  async listIndexedDBFiles() {
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction(['files'], 'readonly')
      const store = transaction.objectStore('files')
      const request = store.getAll()
      
      request.onsuccess = () => {
        const files = request.result.map(file => ({
          name: file.name,
          size: file.size,
          timestamp: file.timestamp,
          type: file.type
        }))
        resolve(files)
      }
      
      request.onerror = () => {
        console.error('列出IndexedDB文件失败:', request.error)
        reject(request.error)
      }
    })
  }

  // 列出localStorage文件
  async listLocalStorageFiles() {
    const files = []
    const keys = Object.keys(localStorage)
    
    keys.forEach(key => {
      if (!key.includes('_chunk_') && !key.includes('_chunks')) {
        const data = localStorage.getItem(key)
        if (data) {
          try {
            const parsed = JSON.parse(data)
            files.push({
              name: key,
              size: new Blob([data]).size,
              timestamp: new Date().toISOString(),
              type: 'application/json'
            })
          } catch (error) {
            // 忽略非JSON数据
          }
        }
      }
    })
    
    return files
  }

  // 获取存储统计信息
  async getStorageStats() {
    try {
      const files = await this.listFiles()
      const totalSize = files.reduce((sum, file) => sum + file.size, 0)
      
      return {
        fileCount: files.length,
        totalSize: totalSize,
        totalSizeMB: (totalSize / 1024 / 1024).toFixed(2),
        files: files
      }
    } catch (error) {
      console.error('获取存储统计失败:', error)
      return {
        fileCount: 0,
        totalSize: 0,
        totalSizeMB: '0.00',
        files: []
      }
    }
  }

  // 清理旧文件
  async cleanupOldFiles(daysToKeep = 30) {
    try {
      const files = await this.listFiles()
      const cutoffDate = new Date()
      cutoffDate.setDate(cutoffDate.getDate() - daysToKeep)
      
      let cleanedCount = 0
      for (const file of files) {
        const fileDate = new Date(file.timestamp)
        if (fileDate < cutoffDate) {
          await this.deleteFile(file.name)
          cleanedCount++
        }
      }
      
      console.log(`✅ 清理了 ${cleanedCount} 个旧文件`)
      return cleanedCount
    } catch (error) {
      console.error('清理旧文件失败:', error)
      return 0
    }
  }

  // 导出所有数据
  async exportAllData() {
    try {
      const files = await this.listFiles()
      const exportData = {}
      
      for (const file of files) {
        const data = await this.loadFromFile(file.name)
        if (data) {
          exportData[file.name] = {
            data: data,
            metadata: {
              size: file.size,
              timestamp: file.timestamp,
              type: file.type
            }
          }
        }
      }
      
      return exportData
    } catch (error) {
      console.error('导出数据失败:', error)
      throw error
    }
  }

  // 导入数据
  async importData(importData) {
    try {
      let importedCount = 0
      
      for (const [fileName, fileData] of Object.entries(importData)) {
        await this.saveToFile(fileName, fileData.data)
        importedCount++
      }
      
      console.log(`✅ 导入了 ${importedCount} 个文件`)
      return importedCount
    } catch (error) {
      console.error('导入数据失败:', error)
      throw error
    }
  }

  // 字符串分块函数
  chunkString(str, chunkSize) {
    const chunks = []
    for (let i = 0; i < str.length; i += chunkSize) {
      chunks.push(str.slice(i, i + chunkSize))
    }
    return chunks
  }

  // 检查存储空间
  async checkStorageSpace() {
    try {
      if (this.db) {
        return await this.checkIndexedDBSpace()
      } else {
        return await this.checkLocalStorageSpace()
      }
    } catch (error) {
      console.error('检查存储空间失败:', error)
      return { used: 0, available: 0, usagePercent: 0 }
    }
  }

  // 检查IndexedDB空间
  async checkIndexedDBSpace() {
    try {
      const stats = await this.getStorageStats()
      const estimatedQuota = 200 * 1024 * 1024 // 200MB estimated quota
      const usagePercent = (stats.totalSize / estimatedQuota) * 100
      
      return {
        used: stats.totalSize,
        available: estimatedQuota - stats.totalSize,
        usagePercent: Math.min(usagePercent, 100)
      }
    } catch (error) {
      return { used: 0, available: 0, usagePercent: 0 }
    }
  }

  // 检查localStorage空间
  async checkLocalStorageSpace() {
    try {
      const used = JSON.stringify(localStorage).length
      const maxSize = 5 * 1024 * 1024 // 5MB
      const usagePercent = (used / maxSize) * 100
      
      return {
        used: used,
        available: maxSize - used,
        usagePercent: Math.min(usagePercent, 100)
      }
    } catch (error) {
      return { used: 0, available: 0, usagePercent: 0 }
    }
  }
}

// 创建全局实例
const localCloudService = new LocalCloudService()

export default localCloudService
